import React, { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { generateExecutiveSummary, saveManualExecutiveSummary, type ExecutiveSummaryData } from '../services/projectService';

// Configure marked to output cleaner HTML
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

// Helper function to remove duplicate title from AI-generated content
const removeDuplicateTitle = (text: string, projectName: string): string => {
  // Remove lines like "Executive Summary: Project Name" or "# Executive Summary: Project Name"
  const lines = text.split('\n');
  const cleanedLines = lines.filter((line, index) => {
    // Only check first few lines for title
    if (index > 3) return true;

    const trimmed = line.trim();
    // Check if line is a title variant
    const isTitleLine =
      trimmed.toLowerCase().startsWith('executive summary') ||
      trimmed.toLowerCase().startsWith('# executive summary') ||
      trimmed.toLowerCase().startsWith('## executive summary');

    return !isTitleLine;
  });

  return cleanedLines.join('\n').trim();
};

// Helper function to safely parse and sanitize markdown
const parseMarkdown = (text: string): string => {
  try {
    const result = marked.parse(text);
    return DOMPurify.sanitize(typeof result === 'string' ? result : '');
  } catch (error) {
    console.error('Markdown parsing error:', error);
    return DOMPurify.sanitize(text);
  }
};

// React PDF Styles
const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 20,
    fontFamily: 'Helvetica',
    fontSize: 10,
    lineHeight: 1.0,
  },
  header: {
    backgroundColor: '#475569', // slate-700
    padding: 20,
    marginTop: -40,
    marginHorizontal: -20,
    marginBottom: 20,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 12,
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#ffffff',
  },
  creatorSection: {
    position: 'absolute',
    right: 20,
    top: 20,
  },
  creatorLabel: {
    fontSize: 8,
    color: '#ffffff',
    marginBottom: 2,
  },
  creatorName: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  content: {
    paddingTop: 0,
  },
  paragraph: {
    marginBottom: 10,
    color: '#334155', // slate-700
    lineHeight: 1.0,
  },
  heading1: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b', // slate-800
    marginTop: 20,
    marginBottom: 10,
  },
  heading2: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b', // slate-800
    marginTop: 16,
    marginBottom: 8,
  },
  heading3: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b', // slate-800
    marginTop: 12,
    marginBottom: 6,
  },
  bulletList: {
    marginBottom: 4,
  },
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  bulletDot: {
    width: 15,
    fontSize: 10,
    color: '#334155',
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    color: '#334155',
    lineHeight: 1.0,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 20,
    right: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#94a3b8', // slate-400
  },
});

// Helper to parse markdown text into React PDF components
const renderMarkdownContent = (text: string) => {
  const lines = text.split('\n');
  const elements: any[] = [];
  let currentBulletList: any[] = [];

  const flushBulletList = () => {
    if (currentBulletList.length > 0) {
      elements.push(
        <View key={`bullets-${elements.length}`} style={pdfStyles.bulletList}>
          {currentBulletList}
        </View>
      );
      currentBulletList = [];
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushBulletList();
      return;
    }

    // Check for headers with # symbols
    const headerMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headerMatch) {
      flushBulletList();
      const level = headerMatch[1].length;
      const headerText = headerMatch[2].replace(/\*\*/g, '');
      const style = level === 1 ? pdfStyles.heading1 : level === 2 ? pdfStyles.heading2 : pdfStyles.heading3;
      elements.push(<Text key={`header-${index}`} style={style}>{headerText}</Text>);
      return;
    }

    // Check for headers ending with colon (e.g., "Overview:", "Purpose and Objectives:")
    const colonHeaderMatch = trimmed.match(/^([^:]+):$/);
    if (colonHeaderMatch) {
      flushBulletList();
      const headerText = colonHeaderMatch[1].replace(/\*\*/g, '').trim();
      // Use heading2 style for colon-style headers
      elements.push(<Text key={`header-${index}`} style={pdfStyles.heading2}>{headerText}</Text>);
      return;
    }

    // Check for bullet points
    const bulletMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      const bulletText = bulletMatch[1].replace(/\*\*/g, '');
      currentBulletList.push(
        <View key={`bullet-${index}`} style={pdfStyles.bulletItem}>
          <Text style={pdfStyles.bulletDot}>•</Text>
          <Text style={pdfStyles.bulletText}>{bulletText}</Text>
        </View>
      );
      return;
    }

    // Regular paragraph
    flushBulletList();
    const cleanText = trimmed.replace(/\*\*/g, '');
    if (cleanText) {
      elements.push(<Text key={`para-${index}`} style={pdfStyles.paragraph}>{cleanText}</Text>);
    }
  });

  flushBulletList();
  return elements;
};

// React PDF Document Component
const ExecutiveSummaryPDFDocument = ({
  projectName,
  content,
  creatorName,
  startDate,
  targetCompletionDate,
  estimatedDays
}: {
  projectName: string;
  content: string;
  creatorName: string;
  startDate?: string;
  targetCompletionDate?: string;
  estimatedDays?: number | null;
}) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.header}>
        <Text style={pdfStyles.headerTitle}>Executive Summary</Text>
        <Text style={pdfStyles.headerSubtitle}>{projectName}</Text>
        <View style={pdfStyles.creatorSection}>
          <Text style={pdfStyles.creatorLabel}>Created by</Text>
          <Text style={pdfStyles.creatorName}>{creatorName}</Text>
        </View>
      </View>
      {startDate && targetCompletionDate && estimatedDays != null && (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 14 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 6, fontFamily: 'Helvetica-Bold', color: '#94a3b8', letterSpacing: 1.5, marginBottom: 5, textTransform: 'uppercase' }}>PROJECT TIMELINE</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
              <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#334155' }}>{startDate}</Text>
              <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#334155' }}>{targetCompletionDate}</Text>
            </View>
            <View style={{ height: 5, backgroundColor: '#6366f1', borderRadius: 3 }} />
          </View>
          <View style={{ width: 50, height: 50, borderRadius: 8, backgroundColor: '#eef2ff', borderWidth: 1, borderColor: '#c7d2fe', marginLeft: 14, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#4338ca' }}>{String(estimatedDays)}</Text>
            <Text style={{ fontSize: 5, fontFamily: 'Helvetica-Bold', color: '#6366f1', letterSpacing: 0.8 }}>EST. DAYS</Text>
          </View>
        </View>
      )}
      {startDate && targetCompletionDate && estimatedDays != null && (
        <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#cbd5e1', marginBottom: 12 }} />
      )}
      <View style={pdfStyles.content}>
        {renderMarkdownContent(content)}
      </View>
      <View style={pdfStyles.footer}>
        <Text>Generated {new Date().toLocaleDateString()}</Text>
        <Text>Page 1</Text>
      </View>
    </Page>
  </Document>
);

/**
 * Modal States:
 * - choice: Initial choice between AI Generate or Manual Write
 * - ai-generating: AI is processing the generation
 * - ai-result: AI generation complete, showing result with regenerate/edit options
 * - manual-write: Manual text editor for writing summary
 * - view: Display existing summary with metadata
 */
type ModalState = 'choice' | 'ai-generating' | 'ai-result' | 'manual-write' | 'view';

interface ExecutiveSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  token: string;
  existingSummary?: string | null;
  existingMethod?: 'ai' | 'manual' | null;
  existingGeneratedAt?: string | null;
  existingTokensUsed?: number | null;
  existingModel?: string | null;
  existingGeneratedBy?: string | null; // User name who created the summary
  currentUserName?: string; // Current logged-in user's name
  onSummaryUpdated: () => void; // Callback to refresh project data
  readOnly?: boolean; // When true, hide edit/regenerate controls
  startDate?: string | null;
  targetCompletionDate?: string | null;
}

export const ExecutiveSummaryModal: React.FC<ExecutiveSummaryModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  token,
  existingSummary,
  existingMethod,
  existingGeneratedAt,
  existingTokensUsed,
  existingModel,
  existingGeneratedBy,
  currentUserName,
  onSummaryUpdated,
  readOnly,
  startDate,
  targetCompletionDate
}) => {
  // Determine initial state based on existing summary
  const getInitialState = (): ModalState => {
    if (existingSummary) return 'view';
    return 'choice';
  };

  const [modalState, setModalState] = useState<ModalState>(getInitialState());
  const [manualText, setManualText] = useState<string>(existingSummary || '');
  const [aiGeneratedText, setAiGeneratedText] = useState<string>('');
  const [aiMetadata, setAiMetadata] = useState<{ tokens?: number; model?: string }>({});
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  // Ref for the document content to export
  const documentRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens/closes or existing summary changes
  useEffect(() => {
    if (isOpen) {
      setModalState(getInitialState());
      setManualText(existingSummary || '');
      setAiGeneratedText('');
      setAiMetadata({});
      setError('');
      setIsSubmitting(false);
    }
  }, [isOpen, existingSummary]);

  // Timeline helpers
  const computeEstimatedDays = (start: string, end: string): number | null => {
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) return null;
    return Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  };

  const formatTimelineDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderTimeline = () => {
    if (!startDate || !targetCompletionDate) return null;
    const days = computeEstimatedDays(startDate, targetCompletionDate);
    if (days === null) return null;

    return (
      <div className="px-6 py-5 bg-slate-50 border-b border-slate-200">
        <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mb-3">Project Timeline</p>
        <div className="flex items-center gap-5">
          {/* Timeline bar section */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-slate-700">{formatTimelineDate(startDate)}</span>
              <span className="text-xs font-semibold text-slate-700">{formatTimelineDate(targetCompletionDate)}</span>
            </div>
            <div className="relative h-2.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 shadow-sm">
              {/* Start dot */}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-3.5 h-3.5 rounded-full bg-white border-2 border-indigo-500 shadow-sm" />
              {/* End dot */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-3.5 h-3.5 rounded-full bg-white border-2 border-blue-500 shadow-sm" />
            </div>
          </div>
          {/* Hero badge */}
          <div className="shrink-0 flex flex-col items-center justify-center w-20 h-20 rounded-xl bg-indigo-50 border border-indigo-200">
            <span className="text-2xl font-bold text-indigo-700 leading-none">{days}</span>
            <span className="text-[10px] font-semibold text-indigo-500 mt-1 tracking-wide">Est. Days</span>
          </div>
        </div>
      </div>
    );
  };

  const handleClose = () => {
    setError('');
    setIsSubmitting(false);
    onClose();
  };

  const handleAIGenerate = async () => {
    setModalState('ai-generating');
    setError('');

    try {
      const result: ExecutiveSummaryData = await generateExecutiveSummary(projectId, token);
      setAiGeneratedText(result.executive_summary);
      setAiMetadata({
        tokens: result.tokens_used,
        model: result.model
      });
      setModalState('ai-result');
      onSummaryUpdated(); // Refresh project data
    } catch (err: any) {
      setError(err.message || 'Failed to generate executive summary. Please try again.');
      setModalState('choice');
    }
  };

  const handleManualSave = async () => {
    if (!manualText.trim()) {
      setError('Please enter a summary before saving.');
      return;
    }

    if (manualText.trim().length < 50) {
      setError('Summary must be at least 50 characters.');
      return;
    }

    if (manualText.trim().length > 10000) {
      setError('Summary cannot exceed 10,000 characters.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await saveManualExecutiveSummary(projectId, { executive_summary: manualText }, token);
      onSummaryUpdated(); // Refresh project data
      setModalState('view');
    } catch (err: any) {
      setError(err.message || 'Failed to save executive summary. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditExisting = () => {
    setManualText(existingSummary || '');
    setModalState('manual-write');
  };

  const handleRegenerateAI = () => {
    setModalState('choice');
  };

  const handleExportPDF = async () => {
    setIsExportingPDF(true);

    try {
      // Get the summary text (either AI generated or existing)
      const rawSummaryText = modalState === 'ai-result' ? aiGeneratedText : (existingSummary || '');
      // Remove duplicate title from the content
      const summaryText = removeDuplicateTitle(rawSummaryText, projectName);

      // Get creator name
      const creatorName = modalState === 'ai-result'
        ? (currentUserName || 'AI Assistant')
        : (existingGeneratedBy || 'Unknown');

      // Compute timeline data for PDF
      const pdfStartDate = startDate ? formatTimelineDate(startDate) : undefined;
      const pdfTargetDate = targetCompletionDate ? formatTimelineDate(targetCompletionDate) : undefined;
      const pdfDays = startDate && targetCompletionDate ? computeEstimatedDays(startDate, targetCompletionDate) : null;

      // Create React PDF document
      const doc = (
        <ExecutiveSummaryPDFDocument
          projectName={projectName}
          content={summaryText}
          creatorName={creatorName}
          startDate={pdfStartDate}
          targetCompletionDate={pdfTargetDate}
          estimatedDays={pdfDays}
        />
      );

      // Generate PDF blob
      const blob = await pdf(doc).toBlob();

      // Generate filename
      const fileName = `${projectName.replace(/[^a-z0-9]/gi, '_')}_Executive_Summary_${new Date().toISOString().split('T')[0]}.pdf`;

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();

      // Cleanup
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF export error:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Executive Summary Test (React PDF)</h2>
              <p className="mt-1 text-sm text-gray-600">{projectName}</p>
            </div>
            <div className="flex items-center space-x-3">
              {/* AI Generation Indicator (not exported) */}
              {(modalState === 'ai-result' || (modalState === 'view' && existingMethod === 'ai')) && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  AI Generated
                </span>
              )}
              {(modalState === 'view' && existingMethod === 'manual') && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Manually Written
                </span>
              )}

              {/* Export PDF Button (visible when viewing/showing summary) */}
              {(modalState === 'ai-result' || modalState === 'view') && (
                <button
                  onClick={handleExportPDF}
                  disabled={isExportingPDF}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Export to PDF"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isExportingPDF ? 'Exporting...' : 'Export PDF'}
                </button>
              )}

              {/* Edit Button (visible when viewing existing summary, hidden in readOnly) */}
              {modalState === 'view' && !readOnly && (
                <button
                  onClick={handleEditExisting}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  title="Edit summary"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              )}

              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* State: Choice */}
          {modalState === 'choice' && !readOnly && (
            <div className="space-y-4">
              <p className="text-gray-700 mb-6">
                How would you like to create the executive summary for this project?
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* AI Generate Option */}
                <button
                  onClick={handleAIGenerate}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                >
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-xl font-bold group-hover:scale-110 transition-transform">
                      AI
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900">AI Generate</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Automatically generate a professional executive summary based on your project requirements using AI.
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    Requires at least 3 requirements to generate.
                  </p>
                </button>

                {/* Manual Write Option */}
                <button
                  onClick={() => setModalState('manual-write')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
                >
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900">Write Manually</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Write your own custom executive summary with full control over the content and messaging.
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    50-10,000 characters required.
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Read-only empty state */}
          {modalState === 'choice' && readOnly && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 font-medium">No executive summary has been created yet</p>
              <p className="text-sm text-gray-400 mt-1">A project manager or admin can generate one.</p>
            </div>
          )}

          {/* State: AI Generating */}
          {modalState === 'ai-generating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative w-24 h-24 mb-6">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 border-4 border-t-blue-600 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-lg font-bold">
                    AI
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Executive Summary...</h3>
              <p className="text-sm text-gray-600 text-center max-w-md">
                Our AI is analyzing your project requirements and creating a comprehensive executive summary. This typically takes 10-20 seconds.
              </p>
            </div>
          )}

          {/* State: AI Result */}
          {modalState === 'ai-result' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-semibold text-gray-900">AI Generation Complete</span>
                </div>
                {aiMetadata.tokens && (
                  <p className="text-xs text-gray-600">
                    Generated using {aiMetadata.model || 'OpenAI'} ({aiMetadata.tokens.toLocaleString()} tokens)
                  </p>
                )}
              </div>

              {/* Enhanced Executive Summary Display */}
              <div ref={documentRef} className="bg-gradient-to-br from-slate-50 via-white to-blue-50 border-2 border-slate-200 rounded-xl shadow-sm overflow-hidden">
                {/* Document Header */}
                <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-4 border-b border-slate-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">Executive Summary</h3>
                      <p className="text-xs text-slate-300 mt-0.5">{projectName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-300">Created by</p>
                      <p className="text-sm font-medium text-white">{currentUserName || 'AI Assistant'}</p>
                    </div>
                  </div>
                </div>

                {/* Project Timeline */}
                {renderTimeline()}

                {/* Document Body */}
                <div className="px-8 py-6 bg-white">
                  <div
                    className="prose prose-slate max-w-none
                      prose-headings:font-bold prose-headings:text-slate-900
                      prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                      prose-h2:text-xl prose-h2:mb-3 prose-h2:mt-6
                      prose-h3:text-lg prose-h3:mb-2 prose-h3:mt-4
                      prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4
                      prose-strong:text-slate-900 prose-strong:font-semibold
                      prose-ul:my-3 prose-li:text-slate-700"
                    style={{
                      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                      fontSize: '15px',
                      lineHeight: '1.7'
                    }}
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(removeDuplicateTitle(aiGeneratedText, projectName)) }}
                  />
                </div>

                {/* Document Footer */}
                <div className="bg-slate-50 px-6 py-3 border-t border-slate-200">
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-slate-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      Generated {new Date().toLocaleString()}
                    </span>
                    <span className="text-slate-500">Powered by {aiMetadata.model || 'OpenAI'}</span>
                  </div>
                </div>
              </div>

              {/* Token count and actions (not exported) */}
              <div className="flex items-center justify-between pt-4">
                {aiMetadata.tokens && (
                  <span className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {aiMetadata.tokens.toLocaleString()} tokens used
                  </span>
                )}
                {!readOnly && (
                  <div className="flex space-x-3">
                    <button
                      onClick={handleRegenerateAI}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                    >
                      Regenerate
                    </button>
                    <button
                      onClick={() => {
                        setManualText(aiGeneratedText);
                        setModalState('manual-write');
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                    >
                      Edit & Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* State: Manual Write */}
          {modalState === 'manual-write' && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  Write a professional executive summary (400-600 words recommended). Focus on business value, strategic importance, and key objectives.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Character count: {manualText.length} / 10,000 (minimum: 50)
                </p>
              </div>

              <textarea
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-sans"
                placeholder="Enter your executive summary here..."
              />

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setModalState(existingSummary ? 'view' : 'choice')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleManualSave}
                  disabled={isSubmitting || manualText.trim().length < 50}
                  className="px-6 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save Summary'}
                </button>
              </div>
            </div>
          )}

          {/* State: View */}
          {modalState === 'view' && existingSummary && (
            <div className="space-y-4">
              {/* Enhanced Executive Summary Display */}
              <div ref={documentRef} className="bg-gradient-to-br from-slate-50 via-white to-blue-50 border-2 border-slate-200 rounded-xl shadow-sm overflow-hidden">
                {/* Document Header */}
                <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-4 border-b border-slate-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">Executive Summary</h3>
                      <p className="text-xs text-slate-300 mt-0.5">{projectName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-300">Created by</p>
                      <p className="text-sm font-medium text-white">{existingGeneratedBy || 'Unknown'}</p>
                    </div>
                  </div>
                </div>

                {/* Project Timeline */}
                {renderTimeline()}

                {/* Document Body */}
                <div className="px-8 py-6 bg-white">
                  <div
                    className="prose prose-slate max-w-none
                      prose-headings:font-bold prose-headings:text-slate-900
                      prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                      prose-h2:text-xl prose-h2:mb-3 prose-h2:mt-6
                      prose-h3:text-lg prose-h3:mb-2 prose-h3:mt-4
                      prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4
                      prose-strong:text-slate-900 prose-strong:font-semibold
                      prose-ul:my-3 prose-li:text-slate-700"
                    style={{
                      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                      fontSize: '15px',
                      lineHeight: '1.7'
                    }}
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(removeDuplicateTitle(existingSummary, projectName)) }}
                  />
                </div>

                {/* Document Footer */}
                <div className="bg-slate-50 px-6 py-3 border-t border-slate-200">
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-slate-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      {existingGeneratedAt ? new Date(existingGeneratedAt).toLocaleString() : 'Date not available'}
                    </span>
                    {existingMethod === 'ai' && existingModel && (
                      <span className="text-slate-500">Powered by {existingModel}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Token count and actions (not exported) */}
              {existingMethod === 'ai' && (
                <div className="flex items-center justify-between pt-4">
                  {existingTokensUsed && (
                    <span className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      {existingTokensUsed.toLocaleString()} tokens used
                    </span>
                  )}
                  {!readOnly && (
                    <button
                      onClick={handleRegenerateAI}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                    >
                      Regenerate with AI
                    </button>
                  )}
                </div>
              )}

              {/* Recreate with AI button for manual summaries */}
              {existingMethod === 'manual' && !readOnly && (
                <div className="flex items-center justify-end pt-4">
                  <button
                    onClick={handleRegenerateAI}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Recreate with AI
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer (only show close button in view mode) */}
        {modalState === 'view' && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
